import _ from 'lodash';

import FormElement from './FormElement';
import StyleMixin from './mixins/styleMixins/StyleMixin';
import mixin from '../privates/utils/mixin';
import DisabledMixin from './mixins/DisabledMixin';
import HiddenCollapsedMixin from './mixins/HiddenCollapsedMixin';
import FocusMixin from './mixins/FocusMixin';
import ClickableMixin from './mixins/ClickableMixin';
import * as Utils from './Utils';
import {nullIfNil, getIndexOrUndefined} from '../privates/utils';
import {validate, validateRange} from '../privates/validations/validation';
import * as members from '../privates/privateMembers';
import valueSanitzationAlgorithm from '../privates/utils/valueSanitzationAlgorithm';
import {runValidationsAndSync, runValidationsAndSyncAfterChange} from '../privates/validationsService/validationsService';
import {getOptionsFromFull, emptyStringIfNotInOptions, optionsToDisplay, validateOption} from '../privates/utils/optionsUtils';
import RequiredMixin from './mixins/RequiredMixin';

/**
 * @class RadioButtonGroup
 * @summary Radio button groups are used for selecting one of a number of
 *  options.
 * @description
 *  Radio button groups consist of a list of [options](#Option). Each
 *  [option](#Option) contains a label, which is what the user sees, and a
 *  value, which is what is used in code and stored in you collections.
 * @mixes $w.FormElement
 * @mixes $w.HiddenCollapsedMixin
 * @mixes $w.DisabledMixin
 * @mixes $w.FocusMixin
 * @mixes $w.ClickableMixin
 * @mixes $w.StyleMixin
 * @mixes $w.RequiredMixin
 * @memberof $w
 * @queryable
 * @viewername wysiwyg.viewer.components.inputs.RadioGroup
 * @definitionId wysiwyg.viewer.components.inputs.RadioGroup
 * @tagname RadioButtonGroup
 */
class RadioButtonGroup extends FormElement {

    /**
     * @typedef {external:Object} $w.RadioButtonGroup~Option
     * @summary An object used by the `options` property that contains the attributes of a radio button item.
     * @property {external:String} [value] The value of the radio button option. This is what you use in code and is what is stored in your collections. Mandatory if `label` is not specified.
     * @property {external:String} [label] The label of the radio button option. This is what a user sees. Mandatory if `value` is not specified.
     * @see [options](#options)
     * @see [value](#value)
     */

    /**
     * @member options
     * @label property
     * @syntax
     * get options(): Option[]
     * set options(value: options[]): void
     * @summary Sets or gets the options in a radio button group.
     * @description
     *  Setting the `options` property sets all the [options](#Option)
     *  available to a user.
     *
     *  Set `options` to an empty array (`[]`) to remove the current radio button group options.
     *
     *  Getting the `options` property returns the current
     *  list of [options](#Option) available to a user.
     *
     *  You cannot modify the data array in-place. To add, change, or remove
     *  individual radio button options:
     *
     *    1. Store the value of the `options` property in a variable.
     *    1. Make changes to the options array.
     *    1. Reset the `options` property with the modified array.
     * @snippet [RadioButtonGroup-options_get.es6=Get the list of options and the first option's label and value from a radio button group]
     * @snippet [RadioButtonGroup-options_set.es6=Set the list of options for a radio button group]
     * @snippet [RadioButtonGroup-add.es6=Add an option to a radio button group]
     *  This example retrieves the options of a radio button group, adds a new
     *  option, and then overwrites the old options.
     * @type {$w.RadioButtonGroup~Option[]}
     * @default An empty array
     * @memberof $w.RadioButtonGroup
     * @instance
     */
    get options() {
        const options = Utils.getData(this, 'options');

        return options ? options.map((option) => ({label: option.label, value: option.value})) : options;
    }

  /**
   * @member options
   * @memberof $w.RadioButtonGroup
   * @param {$w.RadioButtonGroup~Option[]} newOptions
   */
    set options(newOptions) {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compType = members._type.get(this).compType;
        const isArrayValid = validate({
            compType,
            functionName: 'options',
            dataToValidate: {options: newOptions},
            compName: this.id
        });
        if (!isArrayValid) {
            return;
        }
        const arrItemsArrValid = _.every(newOptions, (v, i) => {
            return validate({
                compType,
                functionName: 'options',
                dataToValidate: {RadioButton: v},
                shouldCheckMandatoryFields: true,
                itemIndex: i,
                compName: this.id
            }) && validateOption(v, i, 'RadioButton');
        });
        if (!arrItemsArrValid) {
            return;
        }

        const options = nullIfNil(newOptions) ? optionsToDisplay(newOptions) : [];
        const modifiedOptions = options.map((option) => {
            return {
                value: option.value,
                label: option.label,
                type: 'RadioButton'
            };
        });

        const valToValidate = emptyStringIfNotInOptions(modifiedOptions, RMI.getData(compId).value);

        runValidationsAndSync(RMI, compId, valToValidate);
        Utils.setData(this, {options: modifiedOptions});
    }

    /**
     * @member value
     * @label property
     * @syntax
     * get value(): string
     * set value(value: string): void
     * @summary Sets or gets the value of the selected option.
     * @description
     *  Setting the `value` property sets the [`Option`](#Option)
     *  with that value to be the selected option. You must set the `value`
     *  property to a value that exists in the [`options`](#options) list.
     *
     *  To reset the radio button group to have no option
     *  selected, set the `value` property to `null` or `undefined`.
     *
     *  Getting the `value` property returns the value of
     *  the currently selected option. If no value is selected, the
     *  `value` property returns an empty string.
     * @note
     *  Changing a radio button group's `value` in code does not trigger an `onChange` event.
     *
     *  If a radio button group is connected to a dataset, setting the radio button group's `value`
     *  in code does not set the value of the connected field in the dataset.
     *  That means if you use the dataset to perform a submit, the value changed
     *  in code is not reflected in the submitted item.
     *
     *  To submit the new value using a dataset, set the field's value using the
     *  [`setFieldValue()`](wix-dataset.html#setFieldValue) function before
     *  performing the submit.
     * @snippet [RadioButtonGroup-value_get.es6=Get the value of the selected option]
     * @snippet [RadioButtonGroup-value_set.es6=Select an option by value]
     * @type {external:String}
     * @memberof $w.RadioButtonGroup
     * @override
     */
    get value() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const value = RMI.getData(compId).value;

        return emptyStringIfNotInOptions(this.options, value);
    }

  /**
   * @member value
   * @memberof $w.RadioButtonGroup
   * @param {external:String} val
   */
    set value(val) {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compData = RMI.getFullDataWithOverrides(compId);
        const compType = members._type.get(this).compType;
        const value = valueSanitzationAlgorithm(compData, val);
        const isValid = validate({
            compType,
            functionName: 'value',
            dataToValidate: {value},
            compName: this.id
        });

        if (!isValid) {
            return;
        }

        const valToValidate = emptyStringIfNotInOptions(this.options, value);

        runValidationsAndSyncAfterChange(RMI, compId, valToValidate);

        RMI.setData(compId, { value: valToValidate });
    }

    /**
     * @member selectedIndex
     * @label property
     * @syntax
     * get selectedIndex(): Number
     * set selectedIndex(value: Number): void
     * @summary Sets or gets the index of the selected option.
     * @description
     *  Setting the `selectedIndex` property sets the option at
     *  that index to be the selected option. To set one of the options to be
     *  selected, set the `selectedIndex` property to an index between `0` and
     *  `options.length - 1`.
     *
     *  To reset the radio button group to have no option
     *  selected, set the `selectedIndex` property to `undefined`.
     *
     *  Getting the `selectedIndex` property returns the index of
     *  the currently selected option. If no value is selected, the
     *  `selectedIndex` property returns `undefined`.
     * @type {external:Number}
     * @snippet [RadioButtonGroup-selectedIndex_get.es6=Get the index of the selected option]
     * @snippet [RadioButtonGroup-selectedIndex_set.es6=Select the first option by index]
     * @memberof $w.RadioButtonGroup
     * @instance
     * @attr {external:String}
     */
    get selectedIndex() {
        return getIndexOrUndefined(this.options, 'value', Utils.getData(this, 'value'));
    }

  /**
   * @member selectedIndex
   * @memberof $w.RadioButtonGroup
   * @param {external:Number} index
   */
    set selectedIndex(index) {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compType = members._type.get(this).compType;

        const options = getOptionsFromFull.call(this);
        const acceptNil = true;
        if (!validate({
            compType,
            functionName: 'selectedIndex',
            dataToValidate: {selectedIndex: index},
            compName: this.id
        }) || !validateRange({
            functionName: 'selectedIndex',
            propertyName: 'selectedIndex',
            value: index,
            minValue: 0,
            maxValue: options.length - 1,
            acceptNil
        })) {
            return;
        }

        const value = _.isNil(index) ? '' : options[index].value;

        runValidationsAndSyncAfterChange(RMI, compId, value);
        Utils.setData(this, {value});
    }

    toJSON() {
        const {id, type, global, rendered, required, value, options, selectedIndex} = this;
        return {id, type, global, rendered, required, value, options, selectedIndex};
    }

    /**
     * @member style
     * @label property
     * @syntax
     * get style(): Style
     * @summary Gets an object containing information about the radio button group's styles.
     * @description
     *  The following styles can be used with radio button groups:
     *
     *  + [`backgroundColor`]($w.Style.html#backgroundColor)
     *  + [`borderColor`]($w.Style.html#borderColor)
     *  + [`borderWidth`]($w.Style.html#borderWidth)
     *  + [`color`]($w.Style.html#color)
     *
     *
     *  Getting or setting a radio button group's styles, gets or sets the styles of the
     *  radio button group's regular state. It does not set the styles of the radio button group's hover,
     *  focus, error, or disabled states.
     * @snippet [BackgroundColorMixin-backgroundColor_set.es6=Set the background color]
     * @snippet [BackgroundColorMixin-backgroundColor_get.es6=Get the background color]
     * @type {$w.Style}
     * @memberof $w.RadioButtonGroup
     * @readonly
     * @override
     */
}

mixin(RadioButtonGroup.prototype, HiddenCollapsedMixin.prototype);
mixin(RadioButtonGroup.prototype, DisabledMixin.prototype);
mixin(RadioButtonGroup.prototype, FocusMixin.prototype);
mixin(RadioButtonGroup.prototype, ClickableMixin.prototype);
mixin(RadioButtonGroup.prototype, StyleMixin.prototype);
mixin(RadioButtonGroup.prototype, RequiredMixin.prototype);

export default RadioButtonGroup;
